export type Failure = {
  /** A plain reason, short enough for a pill. */
  label: string;
  /** What it means and what to do, for a tooltip or a detail row. */
  detail: string;
  /** The raw signal: "HTTP 529" or the error class. */
  code: string;
};

const STATUS: Record<number, [string, string]> = {
  400: [
    "Request rejected",
    "The provider refused the request as malformed, or too long for the model.",
  ],
  401: [
    "Key rejected",
    "The provider didn't accept the API key. Check that it is valid and not revoked.",
  ],
  402: [
    "Provider billing",
    "The provider account is out of credit or has a billing problem.",
  ],
  403: ["Not allowed", "The key isn't allowed to use this model or endpoint."],
  404: ["Model not found", "The provider doesn't know this model or endpoint."],
  408: ["Timed out", "The request took too long to reach the provider."],
  413: ["Request too large", "The request was bigger than the provider takes."],
  429: [
    "Rate limited",
    "The provider's rate limit was hit. Agents usually wait and retry on their own.",
  ],
  500: ["Provider error", "The provider failed while answering."],
  502: [
    "Provider unreachable",
    "condense couldn't get an answer through to the provider.",
  ],
  503: [
    "Provider unavailable",
    "The provider was down or refusing traffic. Retrying later usually works.",
  ],
  504: ["Provider timed out", "The provider took too long to answer."],
  529: [
    "Provider overloaded",
    "The provider had no capacity for this request. It's on their side; retrying a little later usually works.",
  ],
};

const CLASS: Record<string, [string, string]> = {
  StreamTruncated: [
    "Answer cut off",
    "The provider's stream ended before the answer finished.",
  ],
  UpstreamResponseIncomplete: [
    "Answer cut off",
    "The provider stopped before the answer finished.",
  ],
  UnparseableJSON: [
    "Unreadable answer",
    "The provider answered, but not with valid JSON.",
  ],
  InvalidWebSocketJSON: [
    "Unreadable answer",
    "The provider answered, but not with valid JSON.",
  ],
  UpstreamResponseFailed: [
    "Provider error",
    "The provider reported the response as failed.",
  ],
  ConnectError: [
    "Provider unreachable",
    "condense couldn't open a connection to the provider.",
  ],
};

const DROPPED: [string, string] = [
  "Connection dropped",
  "The connection to the provider broke before the answer finished.",
];
const TIMEOUT: [string, string] = [
  "Timed out",
  "The provider took too long to answer.",
];

function byClass(error: string): [string, string] {
  if (CLASS[error]) return CLASS[error];
  if (/Timeout/.test(error)) return TIMEOUT;
  if (/WebSocket|Protocol|ReadError|Closed/.test(error)) return DROPPED;
  return ["Request failed", "The request failed before it was answered."];
}

/** Why a request failed, in plain words; null when it didn't. */
export function failureOf(
  status: number | null | undefined,
  error: string | null | undefined,
): Failure | null {
  if (!error && (status == null || status === 200)) return null;
  if (error) {
    const [label, detail] = byClass(error);
    return { label, detail, code: status ? `${error}, HTTP ${status}` : error };
  }
  const s = status as number;
  const [label, detail] =
    STATUS[s] ??
    (s >= 500
      ? ["Provider error", "The provider failed while answering."]
      : ["Request rejected", "The provider refused the request."]);
  return { label, detail, code: `HTTP ${s}` };
}
